import { selectHistoryDataLoading } from 'features/Integrations/state/selectors/integrationSelectors';
import { fetchStageImageObjectUrl } from 'features/Integrations/utils/stageImage.utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaPause, FaPlay } from 'react-icons/fa6';
import {
  MdSignalCellular0Bar,
  MdSignalCellular1Bar,
  MdSignalCellular2Bar,
  MdSignalCellular4Bar,
} from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import Popup from 'reactjs-popup';
import { setDataSheetURLParams } from '../DeviceList/utils/url.utils';
import { buildRow, formatTimestamp } from './datasheetRows.utils';

const SignalStrength = ({ rssi }) => {
  const value = parseInt(rssi, 10);
  if (Number.isNaN(value)) return 'No Signal';
  if (value >= -50)
    return (
      <div>
        <MdSignalCellular4Bar size={24} /> (High)
      </div>
    );
  if (value >= -71)
    return (
      <div>
        <MdSignalCellular2Bar size={24} /> (Medium)
      </div>
    );
  if (value >= -100)
    return (
      <div>
        <MdSignalCellular1Bar size={24} /> (Low)
      </div>
    );
  return (
    <div>
      <MdSignalCellular0Bar size={24} /> (No Signal)
    </div>
  );
};

const FloodImageMedia = ({ images, currentImg, playback, onTogglePlayback }) => {
  if (!images || !images[currentImg]) return null;

  return (
    <section className="border border-theme-strong rounded-2 overflow-hidden mb-2">
      <div className="px-2 py-1 border-bottom border-theme-strong bg-primary-shadow">
        <div className="fw-semibold">Camera</div>
        <small className="d-block text-theme-muted">Captured image for the selected record</small>
      </div>
      <div className="p-2">
        <div className="position-relative d-inline-block">
          <img className="img-fluid" src={images[currentImg].url} alt="Aware Flood Sensor" />
          <button
            className="position-absolute top-0 bottom-0 start-0 end-0 d-flex justify-content-center align-items-center"
            onClick={onTogglePlayback}
          >
            {playback ? <FaPause /> : <FaPlay />}
          </button>
        </div>
        <p>{`${new Date(images[currentImg].ts).toLocaleString()} (${currentImg + 1}/${images.length})`}</p>
      </div>
    </section>
  );
};

const useFloodImages = (device) => {
  const [uniqueImages, setUniqueImages] = useState([]);
  const [currentImg, setCurrentImg] = useState(0);
  const [playback, setPlayback] = useState(null);
  const currentImgRef = useRef(0);

  useEffect(() => {
    return () => {
      if (playback) clearInterval(playback);
    };
  }, [playback]);

  useEffect(() => {
    let isMounted = true;
    const loadImages = async () => {
      const imageMap = new Map();
      const data = Array.isArray(device?.data) ? device.data : [];
      for (let i = 0; i < data.length; i++) {
        const dataPoint = data[i];
        const key = dataPoint?.img_link_url;
        if (!key || imageMap.has(key)) continue;
        const imageURL = await fetchStageImageObjectUrl(key);
        if (!imageURL) continue;
        imageMap.set(key, { url: imageURL, index: i, key, ts: dataPoint.ts });
      }

      if (isMounted) {
        setUniqueImages([...imageMap.values()]);
        setCurrentImg(0);
        currentImgRef.current = 0;
      }
    };

    loadImages();
    return () => {
      isMounted = false;
    };
  }, [device]);

  useEffect(() => {
    return () => {
      uniqueImages.forEach((image) => URL.revokeObjectURL(image.url));
    };
  }, [uniqueImages]);

  const syncImagePage = useCallback(
    (page, record) => {
      if (!uniqueImages.length) return;

      const imageIndex = uniqueImages.findIndex((image) => {
        if (record?.img_link_url && image?.key === record.img_link_url) return true;
        return record?.ts != null && image?.ts != null && String(image.ts) === String(record.ts);
      });
      const nextImage = imageIndex >= 0 ? imageIndex : Math.min(page, uniqueImages.length - 1);
      setCurrentImg(nextImage);
      currentImgRef.current = nextImage;
    },
    [uniqueImages],
  );

  const toggleImagePlayback = useCallback(() => {
    if (playback) {
      clearInterval(playback);
      setPlayback(null);
      return;
    }

    const playbackFunc = () => {
      const newImg = (currentImgRef.current + 1) % uniqueImages.length;
      setCurrentImg(newImg);
      currentImgRef.current = newImg;
    };
    setPlayback(setInterval(playbackFunc, 1000));
  }, [playback, uniqueImages.length]);

  return { uniqueImages, currentImg, playback, syncImagePage, toggleImagePlayback };
};

const buildFloodRows = (device, currentData) => [
  buildRow('Device Alias', device?.alias),
  buildRow('Signal Strength (Bars)', <SignalStrength rssi={currentData?.rssi} />),
  buildRow('Battery', `${currentData?.battery} V`),
  buildRow(
    'Rise Rate Indicator',
    currentData?.ffi1 === 1 ? 'Flooding Alert!' : 'No Flooding Alert',
    'rise',
    {
      valueClassName: currentData?.ffi1 === 1 ? 'text-danger' : '',
    },
  ),
  buildRow(
    'Drop Rate Indicator',
    currentData?.drInd === 1 ? 'Drop Rate Alert!' : 'No Drop Rate Alert',
    'drop',
    {
      valueClassName: currentData?.drInd === 1 ? 'text-danger' : '',
    },
  ),
  buildRow(
    'Depth Trigger Indicator',
    (currentData?.depth1 ?? 0) >
      (device?.water_level_adj ?? 0) + (device?.device_settings?.depthDet1 ?? 0)
      ? 'Depth Trigger Alert!'
      : 'No Depth Trigger Alert',
    'depth-trigger',
    {
      valueClassName:
        (currentData?.depth1 ?? 0) >
        (device?.water_level_adj ?? 0) + (device?.device_settings?.depthDet1 ?? 0)
          ? 'text-danger'
          : '',
    },
  ),
  buildRow('Cell Signal Strength', `${currentData?.rssi} dB`),
  buildRow('Node ID', device?.node_id),
  buildRow(
    'GPS Location',
    device?.location ? `${device.location.lat} N ${device.location.lng} E` : 'N/A',
  ),
  buildRow('Elevation', `${device?.elev ?? 0} ft/ ${((device?.elev ?? 0) * 0.3048).toFixed(3)} m`),
  buildRow(
    'Barometric Pressure',
    `${currentData?.baro} mBar/ ${(currentData?.baro * 0.02952998).toFixed(3)} inHg`,
  ),
  buildRow(
    'Water Level Adjustment',
    `${device?.water_level_adj || 0} in/ ${((device?.water_level_adj || 0) / 12).toFixed(3)} ft`,
  ),
  {
    key: 'sensor-depth',
    render: (
      <Popup
        on="hover"
        position="bottom left"
        trigger={
          <div className="d-flex justify-content-between gap-3 py-1 border-bottom border-theme-strong">
            <p className="mb-0 text-theme-muted">Sensor Depth:</p>
            <div className="text-end text-break">{`${currentData?.depth1} in/ ${(currentData?.depth1 / 12).toFixed(3)} ft`}</div>
          </div>
        }
      >
        <div className="bg-white text-black rounded p-2">
          <p className="m-0">Flood Sensor Pressure/Transducer Reading</p>
        </div>
      </Popup>
    ),
  },
  {
    key: 'actual-depth',
    render: (
      <Popup
        on="hover"
        position="bottom left"
        trigger={
          <div className="d-flex justify-content-between gap-3 py-1 border-bottom border-theme-strong">
            <p className="mb-0 text-theme-muted">Actual Depth:</p>
            <div className="text-end text-break">{`${currentData?.sensordepths} in/ ${(currentData?.sensordepths / 12).toFixed(3)} ft`}</div>
          </div>
        }
      >
        <div className="bg-white text-black rounded p-2">
          <p>Adjusted Depths</p>
          <p className="m-0">
            ("Water Level Adjustment + Flood Sensor Pressure/Transducer Reading").
          </p>
        </div>
      </Popup>
    ),
  },
  buildRow(
    'Water Temperature',
    `${currentData?.h2oTemp} °C/ ${((currentData?.h2oTemp * 9) / 5).toFixed(2)} °F`,
  ),
  buildRow(
    'External Device Data',
    currentData?.soilSDI || currentData?.soilTempSDI
      ? `${currentData?.soilSDI ?? 'N/A'}, ${currentData?.soilTempSDI ?? 'N/A'}`
      : 'N/A',
  ),
  buildRow('Gateway Type', device?.gatewayType ? 'FirstNet Cellular' : 'Satellite'),
];

export const useFloodDatasheetBuilder = ({
  device,
  onClose,
  onSettingsOpen,
  footer,
  loadMore,
  hasMore = false,
  isFetching = false,
  error = null,
}) => {
  const historyDataLoading = useSelector(selectHistoryDataLoading);
  const errorMessage = error?.message ?? null;
  const [, setSearchParams] = useSearchParams();
  const { uniqueImages, currentImg, playback, syncImagePage, toggleImagePlayback } =
    useFloodImages(device);

  const handlePageChange = useCallback(
    (page, record) => {
      const recordId = record?.did ?? record?.ts;
      if (device?.id && recordId != null) {
        setSearchParams((prev) => setDataSheetURLParams(prev, device.id, recordId), {
          replace: true,
        });
      }
      syncImagePage(page, record);
    },
    [device?.id, setSearchParams, syncImagePage],
  );

  return useMemo(
    () => ({
      title: 'Device Data',
      records: Array.isArray(device?.data) ? device.data : [],
      loading: historyDataLoading || isFetching,
      loadingMessage: 'Loading device data...',
      emptyMessage: 'No data available for this device.',
      // The fetch failed: say so with the API's own words instead of "no data".
      errorMessage,
      onClose,
      onSettingsOpen,
      footer,
      onLoadMore: loadMore,
      hasMore,
      loadingMore: isFetching,
      getRecordId: (record) => record?.did ?? record?.ts,
      onPageChange: handlePageChange,
      subtitle: ({ ts } = {}) => `Time Stamp: ${formatTimestamp(ts)}`,
      sections: (currentData) => [
        {
          key: 'device-details',
          title: 'Device Details',
          rows: buildFloodRows(device, currentData),
        },
      ],
      media: (
        <FloodImageMedia
          images={uniqueImages}
          currentImg={currentImg}
          playback={playback}
          onTogglePlayback={toggleImagePlayback}
        />
      ),
    }),
    [
      device,
      historyDataLoading,
      isFetching,
      errorMessage,
      onClose,
      onSettingsOpen,
      footer,
      loadMore,
      hasMore,
      handlePageChange,
      uniqueImages,
      currentImg,
      playback,
      toggleImagePlayback,
    ],
  );
};
